import { useState, useEffect, useRef } from "react";
import { Send, Loader2, Bot, FileDown } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const KEY = "vsm-chat-session";

const rich = (t) => t.replace(/^#{1,6}\s*/gm, "").split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p));

function Bubble({ m, i }) {
  const robin = m.role === "assistant";
  return (
    <div className={`flex ${robin ? "justify-start" : "justify-end"}`} data-testid={`vsm-chat-message-${i}`}>
      <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${robin ? "bg-white/10 text-zinc-100" : "bg-white text-zinc-900"}`}>
        {robin && <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Robin</div>}
        {rich(m.text)}
      </div>
    </div>
  );
}

const tokenClient = (session) => ({
  history: () => api.post(`/vsm-chat/${session.id}/history`, { token: session.token }),
  send: (message) => api.post(`/vsm-chat/${session.id}`, { token: session.token, message }),
  report: () => api.post(`/vsm-chat/${session.id}/report`, { token: session.token }, { responseType: "blob" }),
});

export const VsmChat = ({ session, client: custom, label = "VSM-test", doneText, fileName = "VSM-test-uitslag.pdf", onUpdate }) => {
  const client = custom || tokenClient(session);
  const [state, setState] = useState({ messages: [], done: false, answered: null });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const end = useRef(null);
  const started = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const download = async () => {
    setPdfBusy(true);
    try {
      const { data } = await client.report();
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Het rapport kon niet worden gemaakt. Probeer het opnieuw.");
    }
    setPdfBusy(false);
  };

  const send = async (message) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await client.send(message);
      setState(data);
      onUpdate?.(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Robin is even niet bereikbaar. Probeer het opnieuw.");
      return false;
    } finally {
      setBusy(false);
    }
    return true;
  };

  useEffect(() => {
    if (started.current === session.id) return;
    started.current = session.id;
    if (!custom) sessionStorage.setItem(KEY, JSON.stringify(session));
    client.history().then(({ data }) => {
      if (data.messages.length) {
        setState(data);
        setBusy(false);
      } else send("");
    }, () => send(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [state.messages.length, busy]);

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    setState((s) => ({ ...s, messages: [...s.messages, { role: "user", text: t }] }));
    if (!(await send(t))) setText(t);
  };

  return (
    <div className="border border-white/30 flex flex-col h-[640px]" data-testid="vsm-chat">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/20">
        <div className="h-8 w-8 bg-white text-zinc-900 flex items-center justify-center"><Bot className="h-4 w-4" /></div>
        <div className="flex-1">
          <div className="font-grotesk font-semibold">Robin · AI buddy</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}{state.answered ? ` · ${state.answered}/20 vragen` : ""}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="vsm-chat-messages">
        {state.messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        {busy && <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="vsm-chat-typing"><Loader2 className="h-3 w-3 animate-spin" /> Robin typt…</div>}
        {error && <p className="text-sm text-red-400" data-testid="vsm-chat-error">{error}</p>}
        {state.done && (
          <div className="border border-white/40 p-4 text-sm text-zinc-200" data-testid="vsm-chat-done">
            {doneText || "Bedankt voor het doen van de VSM-test. Joost van der Leij van TIOUW.com BV neemt contact met je op voor de verdere stappen."}
          </div>
        )}
        {state.answered >= 20 && (
          <button type="button" onClick={download} disabled={pdfBusy} className="w-full border border-white bg-white text-zinc-900 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 hover:bg-zinc-200 transition-colors" data-testid="vsm-report-download">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download je uitslag als PDF
          </button>
        )}
        <div ref={end} />
      </div>
      <form onSubmit={submit} className="flex border-t border-white/20">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Typ je antwoord…" disabled={busy && !state.messages.length}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
          className="flex-1 bg-transparent px-4 py-3 text-sm text-white placeholder:text-zinc-500 resize-none outline-none" data-testid="vsm-chat-input" />
        <button type="submit" disabled={busy || !text.trim()} className="px-5 bg-white text-zinc-900 disabled:opacity-40 hover:bg-zinc-200 transition-colors" data-testid="vsm-chat-send">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};

export const savedVsmSession = () => {
  try { return JSON.parse(sessionStorage.getItem(KEY)); } catch { return null; }
};
